import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { LogOut, Trash2, MailOpen, RefreshCw, Inbox, ArrowLeft, LoaderCircle, FileText } from "lucide-react";
import { Logo } from "../components/Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TYPE_LABELS = {
  ristrutturazione: "Ristrutturazione",
  "nuova-costruzione": "Nuova Costruzione",
  cappotto: "Cappotto Termico",
  restauro: "Restauro",
  altro: "Altro",
};

const inputCls =
  "w-full rounded-xl border border-line bg-white px-4 py-3 text-sm text-slate-900 placeholder:text-slate-400 outline-none transition-colors duration-300 focus:border-brand";

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem("te_admin_token") || "");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [leads, setLeads] = useState(null);
  const [reviews, setReviews] = useState(null);
  const [busy, setBusy] = useState(false);
  const [loginError, setLoginError] = useState("");

  const headers = { Authorization: `Bearer ${token}` };

  const fetchLeads = useCallback(async () => {
    if (!token) return;
    try {
      const [leadsRes, reviewsRes] = await Promise.all([
        axios.get(`${API}/leads`, { headers }),
        axios.get(`${API}/reviews`),
      ]);
      setLeads(leadsRes.data);
      setReviews(reviewsRes.data);
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem("te_admin_token");
        setToken("");
        setLeads(null);
        setReviews(null);
      } else {
        toast.error("Errore nel caricamento dei dati");
      }
    }
  }, [token]);

  useEffect(() => {
    fetchLeads();
  }, [fetchLeads]);

  const login = async (e) => {
    e.preventDefault();
    setBusy(true);
    setLoginError("");
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem("te_admin_token", data.token);
      setToken(data.token);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setLoginError(typeof detail === "string" ? detail : "Accesso non riuscito");
    } finally {
      setBusy(false);
    }
  };

  const logout = () => {
    localStorage.removeItem("te_admin_token");
    setToken("");
    setLeads(null);
  };

  const markRead = async (id) => {
    await axios.patch(`${API}/leads/${id}/letto`, {}, { headers });
    setLeads((prev) => prev.map((l) => (l.id === id ? { ...l, letto: true } : l)));
  };

  const remove = async (id) => {
    await axios.delete(`${API}/leads/${id}`, { headers });
    setLeads((prev) => prev.filter((l) => l.id !== id));
    toast.success("Richiesta eliminata");
  };

  const removeReview = async (id) => {
    await axios.delete(`${API}/reviews/${id}`, { headers });
    setReviews((prev) => prev.filter((r) => r.id !== id));
    toast.success("Recensione eliminata");
  };

  if (!token) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-surface2/60 px-5 font-body" data-testid="admin-login-page">
        <form onSubmit={login} className="w-full max-w-sm rounded-2xl border border-line bg-white p-8 shadow-xl shadow-slate-900/5">
          <Logo className="h-24" />
          <h1 className="mt-8 font-heading text-xl font-bold text-slate-900">Area Riservata</h1>
          <p className="mt-1 text-sm text-slate-500">Accedi per consultare le richieste di preventivo.</p>
          <label className="mb-2 mt-7 block text-xs font-semibold uppercase tracking-wider text-slate-500" htmlFor="admin-email">Email</label>
          <input id="admin-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} data-testid="admin-email-input" placeholder="admin@tecnoedil.it" />
          <label className="mb-2 mt-4 block text-xs font-semibold uppercase tracking-wider text-slate-500" htmlFor="admin-password">Password</label>
          <input id="admin-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className={inputCls} data-testid="admin-password-input" placeholder="••••••••" />
          {loginError && (
            <p className="mt-4 rounded-lg border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-600" data-testid="admin-login-error">
              {loginError}
            </p>
          )}
          <button type="submit" disabled={busy} data-testid="admin-login-button" className="mt-6 flex w-full items-center justify-center gap-2 rounded-full bg-brand px-5 py-3 text-sm font-semibold text-white transition-colors hover:bg-brand-amber disabled:opacity-60">
            {busy && <LoaderCircle size={16} className="animate-spin" />}
            Accedi
          </button>
          <Link to="/" className="mt-5 flex items-center justify-center gap-1.5 text-xs text-slate-400 transition-colors hover:text-brand" data-testid="admin-back-home-link">
            <ArrowLeft size={13} /> Torna al sito
          </Link>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-surface2/60 font-body text-slate-900" data-testid="admin-dashboard">
      <header className="glass sticky top-0 z-40 border-b border-line">
        <div className="mx-auto flex h-20 max-w-6xl items-center justify-between px-5">
          <Logo className="h-12" />
          <div className="flex items-center gap-2">
            <Link to="/admin/preventivo" className="flex items-center gap-2 rounded-full bg-brand px-4 py-2 text-xs font-semibold text-white transition-colors hover:bg-brand-amber" data-testid="admin-quote-button">
              <FileText size={14} /> Crea preventivo
            </Link>
            <button onClick={fetchLeads} className="flex items-center gap-2 rounded-full border border-line bg-white px-4 py-2 text-xs font-semibold text-slate-600 transition-colors hover:border-brand hover:text-brand" data-testid="admin-refresh-button">
              <RefreshCw size={14} /> Aggiorna
            </button>
            <button onClick={logout} className="flex items-center gap-2 rounded-full border border-line bg-white px-4 py-2 text-xs font-semibold text-slate-600 transition-colors hover:border-red-400 hover:text-red-500" data-testid="admin-logout-button">
              <LogOut size={14} /> Esci
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-5 py-10">
        <h1 className="font-heading text-2xl font-bold">Richieste di preventivo</h1>
        <p className="mt-1 text-sm text-slate-500" data-testid="admin-leads-count">
          {leads ? `${leads.length} richieste totali · ${leads.filter((l) => !l.letto).length} da leggere` : "Caricamento..."}
        </p>

        <div className="mt-8 space-y-4">
          {leads && leads.length === 0 && (
            <div className="flex flex-col items-center gap-3 rounded-2xl border border-dashed border-slate-300 py-16 text-slate-400" data-testid="admin-empty-state">
              <Inbox size={32} />
              <p className="text-sm">Nessuna richiesta per ora.</p>
            </div>
          )}
          {leads?.map((lead) => (
            <article
              key={lead.id}
              data-testid={`lead-card-${lead.id}`}
              className={`rounded-2xl border bg-white p-6 shadow-sm transition-colors ${lead.letto ? "border-line opacity-70" : "border-brand/50"}`}
            >
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div className="flex items-center gap-3">
                    <h2 className="font-heading text-lg font-semibold text-slate-900">{lead.nome}</h2>
                    {!lead.letto && (
                      <span className="rounded-full bg-brand px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-white">Nuova</span>
                    )}
                  </div>
                  <p className="mt-1 text-sm text-slate-500">
                    {lead.email} · {lead.telefono}
                  </p>
                </div>
                <div className="text-right text-xs text-slate-400">
                  <p>{new Date(lead.created_at).toLocaleString("it-IT", { dateStyle: "medium", timeStyle: "short" })}</p>
                  <p className="mt-1 font-semibold text-brand">
                    {TYPE_LABELS[lead.tipo_lavoro] || lead.tipo_lavoro}
                    {lead.metratura ? ` · ${lead.metratura} m²` : ""}
                  </p>
                </div>
              </div>
              <p className="mt-4 rounded-xl bg-surface2 p-4 text-sm leading-relaxed text-slate-700">{lead.messaggio}</p>
              <div className="mt-4 flex gap-2">
                {!lead.letto && (
                  <button onClick={() => markRead(lead.id)} className="flex items-center gap-1.5 rounded-full border border-line px-3.5 py-1.5 text-xs font-semibold text-slate-600 transition-colors hover:border-brand hover:text-brand" data-testid={`lead-read-${lead.id}`}>
                    <MailOpen size={13} /> Segna come letta
                  </button>
                )}
                <button onClick={() => remove(lead.id)} className="flex items-center gap-1.5 rounded-full border border-line px-3.5 py-1.5 text-xs font-semibold text-slate-600 transition-colors hover:border-red-400 hover:text-red-500" data-testid={`lead-delete-${lead.id}`}>
                  <Trash2 size={13} /> Elimina
                </button>
              </div>
            </article>
          ))}
        </div>

        <h2 className="mt-14 font-heading text-2xl font-bold">Recensioni dei clienti</h2>
        <p className="mt-1 text-sm text-slate-500" data-testid="admin-reviews-count">
          {reviews ? `${reviews.length} recensioni pubblicate` : "Caricamento..."}
        </p>
        <div className="mt-8 space-y-4">
          {reviews && reviews.length === 0 && (
            <div className="flex flex-col items-center gap-3 rounded-2xl border border-dashed border-slate-300 py-16 text-slate-400" data-testid="admin-reviews-empty">
              <Inbox size={32} />
              <p className="text-sm">Nessuna recensione per ora.</p>
            </div>
          )}
          {reviews?.map((r) => (
            <article key={r.id} className="rounded-2xl border border-line bg-white p-6 shadow-sm" data-testid={`admin-review-${r.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <h3 className="font-heading text-lg font-semibold text-slate-900">{r.nome}</h3>
                  <p className="mt-1 flex items-center gap-1 text-sm text-amber-500">
                    {"★".repeat(r.stelle)}<span className="text-slate-300">{"★".repeat(5 - r.stelle)}</span>
                    <span className="ml-2 text-xs text-slate-400">{new Date(r.created_at).toLocaleDateString("it-IT", { dateStyle: "medium" })}</span>
                  </p>
                </div>
                <button onClick={() => removeReview(r.id)} className="flex items-center gap-1.5 rounded-full border border-line px-3.5 py-1.5 text-xs font-semibold text-slate-600 transition-colors hover:border-red-400 hover:text-red-500" data-testid={`review-delete-${r.id}`}>
                  <Trash2 size={13} /> Elimina
                </button>
              </div>
              <p className="mt-4 rounded-xl bg-surface2 p-4 text-sm leading-relaxed text-slate-700">{r.commento}</p>
            </article>
          ))}
        </div>
      </main>
    </div>
  );
}
